import type { OverviewStats } from "@/services/api/guardiansv2/types";
import { kindBadgeClass, kindLabel } from "./chartTheme";

export interface OutcomeSplitBarsProps {
  overview: OverviewStats | null;
}

function FunnelBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{label}</span>
        <span className="font-mono tabular-nums text-slate-300">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5">
        <div
          className="h-1.5 rounded-full bg-indigo-400/70"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

export function OutcomeSplitBars({ overview }: OutcomeSplitBarsProps) {
  if (!overview) {
    return <p className="text-sm text-slate-400">No rule outcome data.</p>;
  }

  const funnel = overview.funnel;
  const funnelMax = Math.max(funnel.checks_evaluated, 1);
  const maxFailedKind = Math.max(...overview.check_kind_splits.map((s) => s.failed_count), 1);

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div className="space-y-3">
        <h4 className="text-[10px] uppercase tracking-wide text-slate-500">Failed checks by rule type</h4>
        {overview.check_kind_splits.map((split) => (
          <div
            key={split.kind}
            className="rounded-xl border border-white/10 bg-slate-900/40 px-3 py-2.5"
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide ${kindBadgeClass(split.kind)}`}
              >
                {kindLabel(split.kind)}
              </span>
              <span className="font-mono text-xs tabular-nums text-slate-200">{split.failed_count}</span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-white/5">
              <div
                className="h-1 rounded-full bg-indigo-400/60"
                style={{ width: `${Math.min(100, (split.failed_count / maxFailedKind) * 100)}%` }}
              />
            </div>
          </div>
        ))}
        {overview.check_kind_splits.length === 0 ? (
          <p className="text-xs text-slate-500">No failed rule checks in this period.</p>
        ) : null}
      </div>
      <div className="space-y-2.5">
        <h4 className="text-[10px] uppercase tracking-wide text-slate-500">Screening funnel</h4>
        <FunnelBar label="Checks evaluated" value={funnel.checks_evaluated} max={funnelMax} />
        <FunnelBar label="Failed checks" value={funnel.failed} max={funnelMax} />
        <FunnelBar label="Ignored (rules)" value={funnel.ignored} max={funnelMax} />
        <FunnelBar label="Alerts raised" value={funnel.alerts_raised} max={funnelMax} />
      </div>
    </div>
  );
}
